import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

// Reusable utilities (consider moving these to a shared utils file)
const SERVER_URL = process.env.REACT_APP_SERVER_HOST;
const isLocalhost = window.location.hostname === "localhost";

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
  return null;
};

const deleteCookie = (name: string) => {
  const cookieOptions = isLocalhost
    ? "path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT"
    : "domain=.vorlas.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie = `${name}=; ${cookieOptions}`;
};

interface UserData {
  nick: string;
  email: string;
  avatar: string;
}

const Profile: React.FC = () => {
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      setError(null);
      const token = getCookie("auth_token");

      if (!token) {
        navigate("/"); // Redirect to home if not logged in
        return;
      }

      try {
        const response = await fetch(`${SERVER_URL}/auth/user`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          // Handle potential expired/invalid token
          deleteCookie("auth_token");
          deleteCookie("nickname");
          navigate("/"); // Redirect to home
          throw new Error("Failed to fetch user data");
        }

        const userData = await response.json();
        setUser(userData);
      } catch (err) {
        console.error("Error fetching user data:", err);
        setError("Could not load profile information.");
        // Keep user potentially logged out if fetch fails
        setUser(null);
        // Optionally redirect after error
        // navigate("/");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        Loading profile...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center mt-10 text-red-500">
        <p>{error}</p>
        <Link
          to="/"
          className="text-blue-500 hover:underline mt-4 inline-block"
        >
          Go back home
        </Link>
      </div>
    );
  }

  if (!user) {
    // Should have been redirected, but as a fallback
    return (
      <div className="text-center mt-10">
        <p>Not logged in.</p>
        <Link
          to="/"
          className="text-blue-500 hover:underline mt-4 inline-block"
        >
          Go back home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-2xl mx-auto bg-white shadow-lg rounded-lg p-6">
        <h1 className="text-3xl font-bold mb-6 text-center text-gray-800">
          Your Profile
        </h1>
        <div className="flex flex-col items-center space-y-4">
          {user.avatar && (
            <img
              src={user.avatar}
              alt="Profile Avatar"
              className="h-24 w-24 rounded-full border-4 border-blue-500"
              onError={(e) => {
                console.warn(`Failed to load profile avatar: ${user.avatar}`);
              }}
            />
          )}
          <div className="text-center">
            <p className="text-xl font-semibold text-gray-700">
              Nickname: {user.nick}
            </p>
            <p className="text-lg text-gray-600">Email: {user.email}</p>
          </div>
          {/* Add more profile details or settings here if needed */}
        </div>
      </div>
    </div>
  );
};

export default Profile;
